import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItemAvatar,
  ListItemButton,
  Popover,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import { useSpace } from 'src/hooks/useSpace';
import { useTrimWorkspace } from 'src/hooks/useTrimWorkspaceName';
import { setWorkspaceFeaturesLoading } from 'src/slices/workspaceUser';

const ButtonWrapper = styled(Button)(
  ({ theme }) => `
      border: 1px solid;
      border-color: ${alpha(theme.colors.alpha.black[100], 0.1)};
      transition: ${theme.transitions.create(['all'])};
      color: ${theme.palette.secondary.main};
      border-radius: 50px;
      margin: 0;
      padding: ${theme.spacing(0.5, 2, 0.5, 0.5)};
      gap: ${theme.spacing(0)};
      white-space: nowrap;
      overflow: hidden;
      text-align: left;
      text-overflow: ellipsis;
      justify-content: flex-start;
  
      &:hover {
        background: ${alpha(theme.colors.alpha.black[100], 0.1)};
        color: ${theme.sidebar.menuItemColor};
      }
  `
);

function SpaceSwitcher() {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { user, switchWorkspace } = useAuth();
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const getTrimmedWorkspaceName = useTrimWorkspace();

  const space = useSpace();

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleSpaceChange = async (workspaceId: string): Promise<void> => {
    try {
      dispatch(setWorkspaceFeaturesLoading());

      await switchWorkspace(workspaceId);

      handleClose();
    } catch (error) {
      enqueueSnackbar(error.message, {
        variant: 'error'
      });
    }
  };

  const hasMultiple = user.organisations?.length + user.workspaces?.length > 1;
  const hasOneOrganisation = user.organisations?.length === 1;

  if (!hasMultiple) {
    return (
      <ButtonWrapper
        color="primary"
        ref={ref}
        sx={{ width: { xs: '100%', md: 'auto' } }}
      >
        <ListItemAvatar>
          <Avatar alt={space.name} src={space.images[0]?.imageUrl} />
        </ListItemAvatar>
        <Box flexGrow={1} flexShrink={1}>
          {space.name}
        </Box>
      </ButtonWrapper>
    );
  }

  return (
    <>
      <ButtonWrapper
        color="primary"
        ref={ref}
        endIcon={<ArrowDropDownIcon />}
        onClick={handleOpen}
        sx={{ width: { xs: '100%', md: 'auto' } }}
      >
        <ListItemAvatar>
          <Avatar alt={space.name} src={space.images[0]?.imageUrl} />
        </ListItemAvatar>
        <Box flexGrow={1} flexShrink={1}>
          {space.name}
        </Box>
      </ButtonWrapper>

      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <Box
          sx={{
            width: 264
          }}
        >
          <List>
            {user.organisations?.length > 0 && (
              <>
                {user.organisations
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((org) => {
                    return (
                      <ListItemButton
                        key={org._id}
                        sx={{ display: 'flex' }}
                        onClick={() => handleSpaceChange(org._id)}
                        selected={user.selectedSpace === org._id}
                      >
                        <ListItemAvatar>
                          <Avatar
                            alt={org.name}
                            src={org?.images?.[0]?.imageUrl}
                          />
                        </ListItemAvatar>
                        <Typography sx={{ fontWeight: 'bold' }}>
                          {org.name}
                        </Typography>
                      </ListItemButton>
                    );
                  })}
                <Divider sx={{ my: 1 }} />
              </>
            )}
            {user.workspaces
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((w) => {
                return (
                  <ListItemButton
                    key={w._id}
                    sx={{ display: 'flex' }}
                    onClick={() => handleSpaceChange(w._id)}
                    selected={user.selectedSpace === w._id}
                  >
                    {!hasOneOrganisation && (
                      <ListItemAvatar>
                        <Avatar
                          alt={getTrimmedWorkspaceName(w.name)}
                          src={w.images[0]?.imageUrl}
                        />
                      </ListItemAvatar>
                    )}
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {getTrimmedWorkspaceName(w.name)}
                    </Typography>
                  </ListItemButton>
                );
              })}
          </List>
        </Box>
      </Popover>
    </>
  );
}

export default SpaceSwitcher;
